import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import {
  Search,
  Plane,
  Train,
  Bus,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
} from "lucide-react";
import { TransportFormData } from "./transportTypes";
import { transportProviders } from "./transportTypes";
import { transportService } from "@/services/transportService";
import debounce from "lodash/debounce";

interface ProviderBubbleProps {
  name: string;
  selected: boolean;
  onClick: () => void;
}

const ProviderBubble: React.FC<ProviderBubbleProps> = ({
  name,
  selected,
  onClick,
}) => (
  <motion.button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full flex text-[11px] items-center gap-2 transition-all duration-300 ${
      selected
        ? "bg-indigo-600 text-white shadow-md border border-indigo-700"
        : "bg-neutral-50 text-neutral-700 hover:bg-indigo-50 hover:text-indigo-600 border border-transparent hover:border-indigo-200"
    }`}
    whileTap={{ scale: 0.95 }}
  >
    <span className="text-xs capitalize font-medium">{name}</span>
  </motion.button>
);

interface LegStatusButtonProps {
  index: number;
  isComplete: boolean;
  isActive: boolean;
  onClick: () => void;
}

const LegStatusButton: React.FC<LegStatusButtonProps> = ({
  index,
  isComplete,
  isActive,
  onClick,
}) => (
  <button
    onClick={onClick}
    className={`
      px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-2
      duration-200
      ${
        isActive
          ? isComplete
            ? "bg-indigo-100 text-indigo-700"
            : "bg-amber-100 text-amber-700"
          : isComplete
          ? "bg-indigo-50 text-indigo-400 hover:bg-indigo-100 hover:text-indigo-700"
          : "bg-amber-50 text-amber-600 hover:bg-amber-50"
      }
    `}
  >
    <span>Leg {index + 1}</span>
    {isComplete ? (
      <CheckCircle2 className="w-3.5 h-3.5" />
    ) : (
      <AlertCircle className="w-3.5 h-3.5" />
    )}
  </button>
);

interface AirlineResult {
  name: string;
  code: string;
  image: string;
}

interface MultiProviderSelectorProps {
  formData: TransportFormData;
  currentLegIndex: number;
  handleInputChange: (field: string, value: any) => void;
  setCurrentLegIndex: (index: number) => void;
  onValidationChange?: (isValid: boolean) => void;
}

const MultiProviderSelector: React.FC<MultiProviderSelectorProps> = ({
  formData,
  currentLegIndex,
  handleInputChange,
  setCurrentLegIndex,
  onValidationChange,
}) => {
  const [mode, setMode] = useState<"flight" | "train" | "bus">(
    formData.legs[currentLegIndex]?.mode || "flight"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<AirlineResult[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const providers = transportProviders[mode] || [];

  useEffect(() => {
    const currentLegMode = formData.legs[currentLegIndex]?.mode;
    if (currentLegMode) {
      setMode(currentLegMode);
    }
  }, [currentLegIndex]);

  // Check if all legs have providers assigned
  useEffect(() => {
    const allLegsComplete = formData.legs.every((leg) => leg.provider);
    onValidationChange?.(allLegsComplete);
  }, [formData.legs, onValidationChange]);

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || query.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      setSearchError(null);

      try {
        const { data: airlines } = await transportService.airlineSearch(query);
        setSearchResults(airlines);
      } catch (error) {
        console.error("Airline search error:", error);
        setSearchError("Failed to fetch airlines. Please try again.");
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (mode === "flight") {
      debouncedSearch(searchQuery);
    } else {
      setSearchResults([]);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, mode, debouncedSearch]);

  const handleProviderSelect = (
    providerId: string,
    providerName?: string,
    providerImage?: string
  ) => {
    // Update the current leg's provider and mode
    const newLegs = [...formData.legs];
    newLegs[currentLegIndex] = {
      ...newLegs[currentLegIndex],
      provider: providerId,
      providerName: providerName,
      providerImage: providerImage,
      mode: mode,
      departureTime: newLegs[currentLegIndex]?.departureTime || "",
      arrivalTime: newLegs[currentLegIndex]?.arrivalTime || "",
      cost: newLegs[currentLegIndex]?.cost || 0,
      origin: newLegs[currentLegIndex]?.origin || "",
      destination: newLegs[currentLegIndex]?.destination || "",
      originCode: newLegs[currentLegIndex]?.originCode || "",
      destinationCode: newLegs[currentLegIndex]?.destinationCode || "",
      originCoordinates: newLegs[currentLegIndex]?.originCoordinates || {
        latitude: 0,
        longitude: 0,
      },
      destinationCoordinates: newLegs[currentLegIndex]
        ?.destinationCoordinates || {
        latitude: 0,
        longitude: 0,
      },
    };

    // Update all legs at once
    handleInputChange("legs", newLegs);
    setSearchQuery("");
    setSearchResults([]);

    // Move to next leg if available and if all previous legs are complete
    const nextIncompleteIndex = newLegs.findIndex(
      (leg, idx) => idx > currentLegIndex && !leg.provider
    );
    if (nextIncompleteIndex !== -1) {
      setTimeout(() => {
        setCurrentLegIndex(nextIncompleteIndex);
      }, 500);
    } else if (currentLegIndex < Number(formData.numberOfLegs) - 1) {
      setTimeout(() => {
        setCurrentLegIndex(currentLegIndex + 1);
      }, 500);
    }
  };

  const handleModeChange = (newMode: "flight" | "train" | "bus") => {
    setMode(newMode);
    setSearchQuery("");
    setSearchResults([]);
    const newLegs = [...formData.legs];
    newLegs[currentLegIndex] = {
      ...newLegs[currentLegIndex],
      provider: undefined,
      providerName: undefined,
      providerImage: undefined,
      mode: newMode,
      departureTime: newLegs[currentLegIndex]?.departureTime || "",
      arrivalTime: newLegs[currentLegIndex]?.arrivalTime || "",
      cost: newLegs[currentLegIndex]?.cost || 0,
      origin: newLegs[currentLegIndex]?.origin || "",
      destination: newLegs[currentLegIndex]?.destination || "",
    };
    handleInputChange("legs", newLegs);
  };

  const handleRemoveProvider = () => {
    const newLegs = [...formData.legs];
    newLegs[currentLegIndex] = {
      ...newLegs[currentLegIndex],
      provider: undefined,
      providerName: undefined,
      providerImage: undefined,
      mode: mode,
      departureTime: newLegs[currentLegIndex]?.departureTime || "",
      arrivalTime: newLegs[currentLegIndex]?.arrivalTime || "",
      cost: newLegs[currentLegIndex]?.cost || 0,
      origin: newLegs[currentLegIndex]?.origin || "",
      destination: newLegs[currentLegIndex]?.destination || "",
    };
    handleInputChange("legs", newLegs);
  };

  const filteredProviders = providers.filter((provider) =>
    provider.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Legs Status Section */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
        {formData.legs.map((leg, index) => (
          <LegStatusButton
            key={index}
            index={index}
            isComplete={!!leg.provider}
            isActive={currentLegIndex === index}
            onClick={() => setCurrentLegIndex(index)}
          />
        ))}
      </div>

      {/* Main Content Area */}
      <div className="flex flex-col gap-3 flex-1">
        {/* Transport Mode Selection */}
        <div className="flex items-center flex-shrink-0">
          <div className="flex gap-1.5 bg-indigo-50 p-1 rounded-lg border border-indigo-100 w-[300px]">
            {["flight", "train", "bus"].map((transportMode) => {
              const Icon = {
                flight: Plane,
                train: Train,
                bus: Bus,
              }[transportMode];

              return (
                <button
                  key={transportMode}
                  onClick={() =>
                    handleModeChange(
                      transportMode as "flight" | "train" | "bus"
                    )
                  }
                  className={`
                    py-1.5 px-3.5 rounded-lg flex items-center gap-2 text-xs font-medium w-[90px]
                    ${
                      mode === transportMode
                        ? "bg-white shadow-md text-indigo-700 border border-indigo-100"
                        : "text-neutral-600 hover:text-indigo-600 hover:bg-white/50"
                    }
                  `}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  <span className="capitalize">{transportMode}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Provider Selection Area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Search Input - Fixed Height */}
          {!formData.legs[currentLegIndex]?.provider && (
            <div className="flex-shrink-0 h-10">
              {!formData.legs[currentLegIndex]?.provider && (
                <div className="relative h-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    type="text"
                    placeholder={`Search ${mode} providers...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 py-1.5 text-[13px] w-full h-full focus-visible:outline-none focus-visible:ring-0 rounded-lg"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 animate-spin" />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Selected Provider Display - Fixed Height */}
          {formData.legs[currentLegIndex]?.provider && (
            <div className="flex-shrink-0 h-10">
              <AnimatePresence mode="wait">
                {formData.legs[currentLegIndex]?.provider && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="flex items-center h-full"
                  >
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-full">
                      {formData.legs[currentLegIndex]?.providerImage && (
                        <img
                          src={formData.legs[currentLegIndex]?.providerImage}
                          alt={formData.legs[currentLegIndex]?.providerName}
                          className="w-5 h-5 object-contain rounded-full"
                        />
                      )}
                      <span className="text-sm text-indigo-700 font-medium">
                        {formData.legs[currentLegIndex]?.providerName}
                      </span>
                      <button
                        onClick={handleRemoveProvider}
                        className="text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Search Results and Provider Selection - Scrollable Area */}
          <div className="flex-1 overflow-y-auto no-scrollbar mt-2">
            {!formData.legs[currentLegIndex]?.provider && (
              <div className="h-full">
                {/* Search Results */}
                <AnimatePresence>
                  {(searchResults.length > 0 || searchError) &&
                    searchQuery.length >= 2 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-white rounded-lg shadow-lg border border-neutral-200 overflow-y-auto max-h-[150px] no-scrollbar"
                      >
                        {searchError ? (
                          <div className="p-3 text-xs text-red-600 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {searchError}
                          </div>
                        ) : (
                          searchResults.map((result) => (
                            <button
                              key={result.code}
                              onClick={() =>
                                handleProviderSelect(
                                  result.code,
                                  result.name,
                                  result.image
                                )
                              }
                              className="w-full p-3 flex items-center gap-3 hover:bg-neutral-50 transition-colors text-left"
                            >
                              {result.image && (
                                <img
                                  src={result.image}
                                  alt={result.name}
                                  className="w-6 h-6 object-contain rounded-full"
                                />
                              )}
                              <div className="flex flex-row items-center gap-2">
                                <div className="text-[13px] font-normal text-neutral-900">
                                  {result.name}
                                </div>
                                <div className="w-1 h-1 bg-neutral-300 rounded-full"></div>
                                <div className="text-xs text-neutral-500">
                                  {result.code}
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </motion.div>
                    )}
                </AnimatePresence>

                {/* Provider Bubbles for non-flight modes */}
                {mode !== "flight" && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {filteredProviders.map((provider) => (
                      <ProviderBubble
                        key={provider.id}
                        name={provider.name}
                        selected={
                          formData.legs[currentLegIndex]?.provider ===
                          provider.id
                        }
                        onClick={() => handleProviderSelect(provider.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiProviderSelector;
